/**
 * Intelligent quantity scaling for grocery ingredients.
 * Handles increment/decrement with context-aware base units.
 */

import { normalizeQuantityForDisplay, parseQuantity } from "./utils";

/**
 * Base unit mapping for different measurement types.
 * Defines how much to increment/decrement for each unit type.
 * Based on common grocery shopping patterns.
 */
const BASE_UNITS: Record<string, number> = {
  // Weight - Imperial
  lb: 0.25, // Quarter pounds are common
  lbs: 0.25,
  pound: 0.25,
  pounds: 0.25,
  oz: 1, // Whole ounces
  ounce: 1,
  ounces: 1,

  // Weight - Metric
  g: 100, // 100g increments are practical
  gm: 100,
  gram: 100,
  grams: 100,
  kg: 0.25, // Quarter kilograms (250g) are common
  kgs: 0.25,
  kilogram: 0.25,
  kilograms: 0.25,

  // Volume - Imperial
  cup: 0.5, // Half cups
  cups: 0.5,
  tbsp: 1, // Tablespoons by 1
  tablespoon: 1,
  tablespoons: 1,
  tsp: 0.5, // Half teaspoons
  teaspoon: 0.5,
  teaspoons: 0.5,
  pint: 0.5,
  pints: 0.5,
  quart: 1,
  quarts: 1,
  gallon: 1,
  gallons: 1,
  gal: 1,

  // Volume - Metric
  ml: 50, // 50ml increments
  mL: 50,
  milliliter: 50,
  milliliters: 50,
  l: 0.25, // Quarter liters
  L: 0.25,
  liter: 0.25,
  liters: 0.25,

  // Chinese units - Weight
  克: 100, // g equivalent
  公斤: 0.25, // kg equivalent
  千克: 0.25,
  斤: 0.5, // Traditional Chinese catty (~600g, increment by half)
  兩: 1, // Liang (~50g, increment by 1)
  两: 1,
  磅: 0.25, // lb equivalent

  // Chinese units - Count/Measure words
  個: 1,
  个: 1,
  隻: 1,
  只: 1,
  條: 1,
  条: 1,
  根: 1,
  把: 1,
  片: 1,
  塊: 1,
  块: 1,
  包: 1,
  袋: 1,
  瓶: 1,
  罐: 1,
  盒: 1,
  聽: 1,
  听: 1,
  桶: 1,
  箱: 1,
  串: 1,
  顆: 1,
  颗: 1,
  粒: 1,
  枚: 1,
  支: 1,
  張: 1,
  张: 1,
  份: 1,
  碗: 1,
  盤: 1,
  盘: 1,
  碟: 1,
  杯: 1,
  勺: 1,
  匙: 1,
  壺: 1,
  壶: 1,
  鍋: 1,
  锅: 1,
};

/**
 * Extract numeric value and unit from quantity string.
 *
 * @example
 * parseQuantityComponents("2.5kg") // { value: 2.5, unit: "kg", hasSpace: false }
 * parseQuantityComponents("450 g") // { value: 450, unit: "g", hasSpace: true }
 * parseQuantityComponents("3") // { value: 3, unit: null, hasSpace: false }
 * parseQuantityComponents("兩顆") // { value: 2, unit: "顆", hasSpace: false }
 */
function parseQuantityComponents(quantity: string): {
  value: number;
  unit: string | null;
  originalUnit: string | null;
  hasSpace: boolean;
} {
  // Detect if there's a space BEFORE normalizing (normalization removes spaces for short units)
  const hasSpaceBeforeUnit = /^\d+\.?\d*\s+\w/.test(quantity);

  // Now normalize display (converts Chinese numbers to Arabic, removes spaces for short units)
  const normalized = normalizeQuantityForDisplay(quantity);

  // Try to match number + unit pattern with optional space
  // Supports: "2.5kg", "450g", "3", "2顆", etc.
  const numericPattern = /^(\d+\.?\d*)(\s*)(.*)$/;
  const match = normalized.match(numericPattern);

  if (match) {
    const value = parseFloat(match[1]);
    const unit = match[3].trim() || null;
    // Use the detected space from BEFORE normalization
    return { value, unit, originalUnit: unit, hasSpace: hasSpaceBeforeUnit };
  }

  // No numeric pattern found, default to 1
  return { value: 1, unit: null, originalUnit: null, hasSpace: false };
}

/**
 * Determine the base unit (increment/decrement amount) for a given unit type.
 * Falls back to 1 for unknown units.
 *
 * @param unit - The measurement unit (e.g., "lb", "g", "kg", "顆")
 * @returns The base increment/decrement amount
 */
function getBaseUnit(unit: string | null): number {
  if (!unit) return 1; // Unknown/unitless defaults to 1

  // Normalize unit to lowercase for English units
  const normalizedUnit = unit.toLowerCase();

  // Check if we have a specific base unit for this measurement
  if (BASE_UNITS[normalizedUnit] !== undefined) {
    return BASE_UNITS[normalizedUnit];
  }

  // Check Chinese units (case-sensitive)
  if (BASE_UNITS[unit] !== undefined) {
    return BASE_UNITS[unit];
  }

  // Unknown unit type, default to 1
  return 1;
}

/**
 * Scale ingredient quantity up or down intelligently.
 *
 * @param ingredientText - Full ingredient text (e.g., "2kg chicken", "watermelon")
 * @param direction - "increment" or "decrement"
 * @returns Updated ingredient text with scaled quantity
 *
 * @example
 * // Increment examples
 * scaleQuantity("watermelon", "increment") // "2 watermelon"
 * scaleQuantity("2 watermelon", "increment") // "3 watermelon"
 * scaleQuantity("450g pork", "increment") // "550g pork"
 * scaleQuantity("2lb chicken", "increment") // "2.25lb chicken"
 *
 * // Decrement examples
 * scaleQuantity("3 watermelon", "decrement") // "2 watermelon"
 * scaleQuantity("2 watermelon", "decrement") // "watermelon"
 * scaleQuantity("watermelon", "decrement") // "watermelon" (no change, already at minimum)
 * scaleQuantity("550g pork", "decrement") // "450g pork"
 * scaleQuantity("50g pork", "decrement") // "pork" (removed quantity, but remembers unit)
 */
export function scaleQuantity(
  ingredientText: string,
  direction: "increment" | "decrement"
): string {
  // Parse current quantity
  const currentQuantity = parseQuantity(ingredientText);

  if (currentQuantity) {
    // Has a quantity - parse and scale it
    const { value, unit, hasSpace } = parseQuantityComponents(currentQuantity);
    const baseUnit = getBaseUnit(unit);

    let newValue: number;
    if (direction === "increment") {
      newValue = value + baseUnit;
    } else {
      newValue = value - baseUnit;
    }

    // Special case: if decrementing count-based items (no unit) to 1, remove the quantity entirely
    // This handles the watermelon scenario: "2 watermelon" → "watermelon" (not "1 watermelon")
    // Only applies to items without measurement units (count-based items)
    const isCountBasedItem = !unit;
    const shouldRemoveQuantity =
      newValue <= 0 || (direction === "decrement" && isCountBasedItem && newValue === 1);

    if (shouldRemoveQuantity) {
      // Remove quantity entirely, return just the item name
      // Remove the quantity prefix from the original text
      const textWithoutQuantity = ingredientText
        .replace(new RegExp(`^${escapeRegex(currentQuantity)}\\s*(?:of\\s+)?`, "i"), "")
        .trim();

      return textWithoutQuantity;
    }

    // Format the new quantity with the unit - preserve original spacing
    const formattedValue = formatNumber(newValue);
    const spacer = hasSpace ? " " : "";
    const newQuantity = unit ? `${formattedValue}${spacer}${unit}` : `${formattedValue}`;

    // Replace old quantity with new quantity in the original text
    const updatedText = ingredientText.replace(
      new RegExp(`^${escapeRegex(currentQuantity)}`),
      newQuantity
    );

    return updatedText;
  } else {
    // No quantity currently
    if (direction === "decrement") {
      // Already at minimum (no quantity), can't go lower
      return ingredientText;
    } else {
      // Increment from no quantity - add quantity "2"
      // Use count-based increment (default base unit is 1, so 1 + 1 = 2)
      return `2 ${ingredientText}`;
    }
  }
}

/**
 * Scale quantity with memory of previous unit.
 * When quantity goes to zero and then back up, it remembers the unit.
 *
 * @param ingredientText - Current ingredient text
 * @param direction - "increment" or "decrement"
 * @param previousQuantity - Previous quantity string (for context memory)
 * @returns Object with updated text and current quantity for memory
 *
 * @example
 * // Scenario: 450g pork → decrement 5x → pork → increment once
 * const result1 = scaleQuantityWithMemory("450g pork", "decrement", null);
 * // result1 = { text: "350g pork", currentQuantity: "350g" }
 * // ... continue decrementing ...
 * const result2 = scaleQuantityWithMemory("50g pork", "decrement", "150g");
 * // result2 = { text: "pork", currentQuantity: null }
 * const result3 = scaleQuantityWithMemory("pork", "increment", "50g");
 * // result3 = { text: "100g pork", currentQuantity: "100g" }
 */
export function scaleQuantityWithMemory(
  ingredientText: string,
  direction: "increment" | "decrement",
  previousQuantity: string | null
): { text: string; currentQuantity: string | null } {
  const currentQuantity = parseQuantity(ingredientText);

  // Special case: incrementing from no quantity with a previous quantity in memory
  if (!currentQuantity && direction === "increment" && previousQuantity) {
    // Extract unit from previous quantity
    const { unit, hasSpace } = parseQuantityComponents(previousQuantity);
    const baseUnit = getBaseUnit(unit);

    // For count-based items (no unit), start from 2, not from baseUnit
    // This ensures "watermelon" → increment → "2 watermelon" (not "1 watermelon")
    const startValue = !unit ? 2 : baseUnit;

    // Start from appropriate value with the remembered unit
    const formattedValue = formatNumber(startValue);
    const spacer = hasSpace ? " " : "";
    const newQuantity = unit ? `${formattedValue}${spacer}${unit}` : `${formattedValue}`;

    // Detect if original text has Chinese characters - if so, no space before item name
    const isChinese = /[\u4e00-\u9fff]/.test(ingredientText);
    const separator = isChinese ? "" : " ";
    const newText = `${newQuantity}${separator}${ingredientText}`;

    return {
      text: newText,
      currentQuantity: newQuantity,
    };
  }

  // Regular scaling
  const scaledText = scaleQuantity(ingredientText, direction);
  const newQuantity = parseQuantity(scaledText);

  return {
    text: scaledText,
    currentQuantity: newQuantity || null,
  };
}

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Format a number for display, removing unnecessary decimal places.
 *
 * @example
 * formatNumber(2.5) // "2.5"
 * formatNumber(2.0) // "2"
 * formatNumber(2.25) // "2.25"
 * formatNumber(100) // "100"
 */
function formatNumber(value: number): string {
  // Remove trailing zeros and unnecessary decimal point
  return value % 1 === 0 ? value.toString() : value.toFixed(2).replace(/\.?0+$/, "");
}
